import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

interface SchedulingDialogProps {
  visible: boolean
  schedule: any
  onSave: (schedule: any) => void
  onHide: () => void
}

const defaultSchedule = {
  frequency: 'hourly',
  time: '',
  weekdays: [],
  monthdays: [],
  nTimes: '',
  nTimesEnabled: false,
  dateRange: { from: '', to: '' },
  dateRangeEnabled: false,
  summary: '',
}

const SchedulingDialog: React.FC<SchedulingDialogProps> = ({ visible, schedule, onSave, onHide }) => {
  const [localSchedule, setLocalSchedule] = useState(schedule || defaultSchedule)

  const handleSave = () => {
    // Generate a summary string for display
    let summary = ''
    if (localSchedule.nTimesEnabled && localSchedule.nTimes) {
      summary = `Run ${localSchedule.nTimes} times`
    } else if (localSchedule.frequency === 'hourly') summary = 'Every hour'
    else if (localSchedule.frequency === 'minutely') summary = 'Every minute'
    else if (localSchedule.frequency === 'daily') summary = `Every day at ${localSchedule.time || '00:00'}`
    else if (localSchedule.frequency === 'weekly') summary = `Every week on ${localSchedule.weekdays?.join(', ') || 'N/A'} at ${localSchedule.time || '00:00'}`
    else if (localSchedule.frequency === 'monthly') summary = `Every month on ${localSchedule.monthdays?.join(', ') || 'N/A'} at ${localSchedule.time || '00:00'}`
    else summary = 'Custom schedule'
    onSave({ ...localSchedule, summary })
  }

  return (
    <Dialog open={visible} onOpenChange={onHide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Scheduling Options</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="text-xs font-semibold">Frequency</label>
          <select
            className="border rounded p-1"
            value={localSchedule.frequency}
            onChange={e => setLocalSchedule(s => ({ ...s, frequency: e.target.value }))}
          >
            <option value="hourly">Every hour</option>
            <option value="minutely">Every minute</option>
            <option value="daily">Every day</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
            <option value="custom">Custom</option>
          </select>
          {(localSchedule.frequency === 'daily' ||
            localSchedule.frequency === 'weekly' ||
            localSchedule.frequency === 'monthly') && (
            <input
              type="time"
              className="border rounded p-1"
              value={localSchedule.time}
              onChange={e => setLocalSchedule(s => ({ ...s, time: e.target.value }))}
            />
          )}
          {localSchedule.frequency === 'weekly' && (
            <div>
              <label className="text-xs">Weekdays:</label>
              <div className="flex gap-2 flex-wrap">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <label key={day} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={localSchedule.weekdays?.includes(day)}
                      onChange={e => {
                        setLocalSchedule(s => ({
                          ...s,
                          weekdays: e.target.checked
                            ? [...(s.weekdays || []), day]
                            : (s.weekdays || []).filter((d: string) => d !== day),
                        }))
                      }}
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>
          )}
          {localSchedule.frequency === 'monthly' && (
            <div>
              <label className="text-xs">Month days:</label>
              <input
                type="text"
                className="border rounded p-1"
                placeholder="e.g. 1,15,30"
                value={localSchedule.monthdays?.join(',') || ''}
                onChange={e =>
                  setLocalSchedule(s => ({
                    ...s,
                    monthdays: e.target.value.split(',').map((d: string) => d.trim()).filter(Boolean),
                  }))
                }
              />
            </div>
          )}
          {/* N Times Checkbox and Input */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!localSchedule.nTimesEnabled}
              onChange={e =>
                setLocalSchedule(s => ({
                  ...s,
                  nTimesEnabled: e.target.checked,
                  nTimes: e.target.checked ? (s.nTimes || '1') : '',
                }))
              }
              id="ntimes-checkbox"
            />
            <label htmlFor="ntimes-checkbox" className="text-xs">Run N Times</label>
            <input
              type="number"
              className="border rounded p-1 ml-2"
              value={localSchedule.nTimes || ''}
              onChange={e => setLocalSchedule(s => ({ ...s, nTimes: e.target.value }))}
              min={1}
              disabled={!localSchedule.nTimesEnabled}
              style={{ width: 60 }}
            />
          </div>
          {/* Date Range Option */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!localSchedule.dateRangeEnabled}
              onChange={e =>
                setLocalSchedule(s => ({
                  ...s,
                  dateRangeEnabled: e.target.checked,
                  dateRange: e.target.checked ? s.dateRange : { from: '', to: '' },
                }))
              }
              id="daterange-checkbox"
            />
            <label htmlFor="daterange-checkbox" className="text-xs">Enable date range</label>
          </div>
          {localSchedule.dateRangeEnabled && (
            <div>
              <label className="text-xs">Date Range:</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="border rounded p-1"
                  value={localSchedule.dateRange?.from || ''}
                  onChange={e => setLocalSchedule(s => ({
                    ...s,
                    dateRange: { ...s.dateRange, from: e.target.value },
                  }))}
                />
                <span className="mx-1">to</span>
                <input
                  type="date"
                  className="border rounded p-1"
                  value={localSchedule.dateRange?.to || ''}
                  onChange={e => setLocalSchedule(s => ({
                    ...s,
                    dateRange: { ...s.dateRange, to: e.target.value },
                  }))}
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={handleSave}>Save</Button>
          <Button variant="outline" onClick={onHide}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SchedulingDialog
